import Tooltip from '@cpg-its/ads.common.tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { FieldArray, FieldArrayRenderProps, FormikProps } from 'formik';
import React, { useEffect, useState } from 'react';
import { Hidden } from '@material-ui/core';
import Banner from '@cpg-its/ads.containers.banner';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { FormField } from '../../../../../global/components/form-element/form-field';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';
import { tcsmColors } from '../../../../../global/constants/theme/theme-constants/colors';
import { TerminationProps } from '../../../models/arc-epi-props';
import { EmploymentCombinationTypes, TerminationFormValues } from '../models/termination-form-model';
import { labelSupplementStyles } from '../../../../../global/constants/theme/styles/components/label-supplement-styles';
import { DividerSection } from '../../../../../global/components/layout/divider-section';
import { getProductTerminationDates } from '../../../../../global/ducks/benadmin-api';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { YesNoOptions } from '../../../../../global/constants/lists';
import { BenefitEndDate, TerminationTypes } from '../models/employment-termination-model';
import { datesAreOnSameDay, getFormattedDateYmd, getNumberOfDays } from '../../../../../global/helpers/date-helpers';
import { FormGroup } from '../../../../../global/components/layout/form-group';
import { sortTableStyles } from '../../../../../global/constants/theme/styles/tables/generic-sort-table';
import { apiErrorClear } from '../../../../../global/ducks/base-api';
import { formatDate } from '../../../../../global/helpers/form-data-helper';
import { SemaphoreService } from '../../../../../global/helpers/semaphore-helper';
import { replaceAll } from '../../../../../global/helpers/string-helpers';

interface Props {
	formProps: FormikProps<TerminationFormValues>;
	terminationProps: TerminationProps;
	employmentTypeCombo: EmploymentCombinationTypes;
	setPreviousValue: (data: any) => void;
	setCurrentValue: (data: any) => void;
	currentValue: any;
}
export function EmploymentTerminationFields(props: Props) {
	const { formProps, terminationProps, employmentTypeCombo } = props;
	const headerClasses = labelSupplementStyles();
	const tableClasses = sortTableStyles();
	const onCheckboxChange = (e, fieldArrayProps: FieldArrayRenderProps) => {
		const values = formProps.values.employment_types;
		const { value } = e.target;
		if (e.target.checked) {
			fieldArrayProps.push(value);
		} else {
			fieldArrayProps.remove(values.indexOf(value));
		}
	};

	const employementId = useSelector<TcsmStates, number>(state =>
		state?.employmentState?.currentEmployment?.id ?
			state?.employmentState?.currentEmployment?.id :
			state?.employmentState?.nonStipendiaryEmployment?.non_stipendiary_position_id);
	const employeeId = useSelector<TcsmStates, string>(state => state?.employmentState?.currentEmployee?.guid);
	const [productTerminationDates, setProductTerminationDates] = useState<BenefitEndDate[]>(null);
	const [productTerminationDatesLoaded, setProductTerminationDatesLoaded] = useState<boolean>(true);
	const [warningMessage, setWarningMessage] = useState<string>('');

	const dispatch = useDispatch();

	const getWarningMessage = () => {
		const today = new Date();
		const inputDate = new Date(formProps?.values?.termination_date);
		const dayDiff = getNumberOfDays(inputDate, today);
		if (datesAreOnSameDay(inputDate, today) && formProps?.values?.extenuating_circumstances) {
			return terminationProps?.sameDayWarning;
		}
		if (dayDiff > 90 || dayDiff < -90) {
			return terminationProps?.nintyPlusWarning;
		}
		return terminationProps?.nonExtenuatingWarning;
	};

	const bannerContent = formProps?.values?.termination_type === TerminationTypes.LeaveOfAbsence
		? terminationProps?.leaveOfAbsenceWarningTitle : warningMessage;

	const onlyEmploymentNonStip = formProps?.values?.termination_type === TerminationTypes.Employment &&
		formProps?.values?.employment_types.includes(EmploymentCombinationTypes.NonStipendiary) &&
		formProps?.values?.employment_types?.length === 1;


	function GetProductTerminationDatesAsync(): Promise<void> {
		return new Promise<void>((resolve) => {

			dispatch(apiErrorClear());

			dispatch(getProductTerminationDates(
				employeeId,
				employementId,
				getFormattedDateYmd(formProps?.values?.termination_date, true),
				(response) => {
					setProductTerminationDates(response?.data);
					props?.setCurrentValue(formProps?.values);
					props?.setPreviousValue(formProps?.initialValues);
					dispatch(apiErrorClear());

					resolve();
					setProductTerminationDatesLoaded(true);
				},
				() => {
					setProductTerminationDates(null);
					props?.setCurrentValue(formProps?.values);
					props?.setPreviousValue(formProps?.initialValues);
					resolve();
					setProductTerminationDatesLoaded(true);
				},
				null,
				formProps?.values?.termination_type === TerminationTypes.LeaveOfAbsence,
			));

			setWarningMessage(getWarningMessage());
		});
	}

	useEffect(() => {
		if (
			formProps?.values?.termination_date && formProps?.values?.termination_date?.length === 10 &&
			props?.currentValue?.termination_date !== formProps?.values?.termination_date &&
			!onlyEmploymentNonStip
		) {
			setProductTerminationDatesLoaded(false);

        SemaphoreService.ExecuteRequestAsync(GetProductTerminationDatesAsync);
		}

	}, [formProps?.values?.termination_date, formProps?.values?.extenuating_circumstances, formProps?.values?.employment_types]);

	const renderEmploymentTypeOptions = () => {
		if (employmentTypeCombo !== EmploymentCombinationTypes.StipAndNonStip) return null;

		return (
			<FieldArray
				name='employment_types'
				render={(fieldArrayProps) => (
					<>
						<ContentSpacer>
							<h5>{`${terminationProps?.employmentTypeLabel}*`}</h5>
							<p>{terminationProps?.employmentTypePrompt}</p>
						</ContentSpacer>

						<FormField
							id={EmploymentCombinationTypes.NonStipendiary}
							name='employment_types'
							label={terminationProps?.nonStipendiaryText || 'Non-Stipendiary'}
							component='checkBox'
							touched={formProps.touched}
							errors={formProps.errors}
							value={EmploymentCombinationTypes.NonStipendiary}
							checked={formProps.values.employment_types?.includes(EmploymentCombinationTypes.NonStipendiary)}
							checkBoxGroup
							onChange={(e) => onCheckboxChange(e, fieldArrayProps)}

						/>

						<FormField
							id={EmploymentCombinationTypes.Stipendiary}
							name='employment_types'
							label={terminationProps?.stipendiaryText || 'Stipendiary'}
							component='checkBox'
							touched={formProps.touched}
							errors={formProps.errors}
							value={EmploymentCombinationTypes.Stipendiary}
							checked={formProps.values.employment_types?.includes(EmploymentCombinationTypes.Stipendiary)}
							checkBoxGroup
							onChange={(e) => onCheckboxChange(e, fieldArrayProps)}
						/>
					</>
				)}
			/>
		);
	};

	const renderBenefitEndSection = () => {

		return (
			<>
				{
					formProps?.values?.termination_type === TerminationTypes.Employment &&
					<DividerSection>
						<FormGroup>
							{
								!(formProps?.values?.employment_types?.length === 1 && formProps?.values?.employment_types[0] === EmploymentCombinationTypes.NonStipendiary) &&
								<ContentSpacer>
									<FormField
										name='person_retiring_indicator'
										label={terminationProps?.isRetiringLabel}
										component="radioGroup"
										options={YesNoOptions}
										errors={formProps.errors}
										touched={formProps.touched}
										required
									/>
								</ContentSpacer>
							}
							<div className={headerClasses.labelLayout}>
								<p><strong>{terminationProps?.extenuatingLabel}</strong></p>
								<div className='supplement-wrapper'>
									<Tooltip iconColor={tcsmColors.blue} title={terminationProps?.extenuatingTooltip} />
								</div>
							</div>
							<p className='custom-label'>{terminationProps?.extenuatingDescription}</p>
							<FormField
								name='extenuating_circumstances'
								component='input'
								multiline
								rows={4}
								errors={formProps.errors}
								touched={formProps.touched}
							/>

						</FormGroup>
					</DividerSection>
				}
				{!productTerminationDatesLoaded && <LoadingCircle />}
				{productTerminationDates?.length > 0 && !onlyEmploymentNonStip &&
					<DividerSection removeDivider sectionText={terminationProps?.benefitTitle} >
						<div className={tableClasses.sortTable}>
							<Hidden xsDown>
								<div className='sort-table-row'>
									<div className='sort-table-row-cell'>
										<p><strong>{terminationProps?.planTypeLabel}</strong></p>
									</div>
									<div className='sort-table-row-cell'>
										<p><strong>{terminationProps?.terminateDateLabel}</strong></p>
									</div>
								</div>
							</Hidden>
							<div className='sort-table-content'>
								{
									productTerminationDates?.map(a =>
										<div className='sort-table-row' key={a.plan_type}>
											<div className='sort-table-row-cell'>
												<Hidden smUp>
													<p><strong>{terminationProps?.planTypeLabel}</strong></p>
												</Hidden>
												<p>{a?.plan_name}</p>
											</div>
											<div className='sort-table-row-cell'>
												<Hidden smUp>
													<p><strong>{terminationProps?.terminateDateLabel}</strong></p>
												</Hidden>
												<p>{a?.reason || formatDate(replaceAll(a?.end_date, '-', '/'), true, true)}</p>
											</div>
										</div>)
								}
							</div>
						</div>
					</DividerSection>}
			</>
		);
	};
	return (
		<>
			{
				formProps?.values?.termination_type === TerminationTypes.Employment && renderEmploymentTypeOptions()
			}
			<DividerSection removeDivider>

				<FormGroup>
					<FormField
						name='termination_date'
						label={
							formProps?.values?.termination_type === TerminationTypes.Employment ?
								terminationProps?.lastDayWorkedLabel :
								terminationProps?.effectiveDateLabel
						}
						component='datePicker'
						touched={formProps.touched}
						errors={formProps.errors}
						required
						autoWidth
						tooltip={
							formProps?.values?.termination_type === TerminationTypes.LeaveOfAbsence ?
								{
									title: terminationProps.unpaidLeaveEffectiveDateLabel,
								} :
								null
						}
					/>
				</FormGroup>
				{
					formProps?.values?.termination_type === TerminationTypes.Employment &&
					<ContentSpacer>
						<div className={headerClasses.labelLayout}>
							<p className='caption'><strong>{terminationProps?.dontKnowDateText}</strong></p>
							<div className='supplement-wrapper'>
								<Tooltip iconColor={tcsmColors.blue} title={terminationProps?.dontKnowTerminationDateTooltip} />
							</div>
						</div>
					</ContentSpacer>
				}
				{
					formProps.values.termination_date &&
					renderBenefitEndSection()
				}

				{
					formProps?.values?.termination_date && bannerContent && productTerminationDates &&
					<Banner banner={{
						type: 'warning',
						label:
							<div className='rich-text'>
								<WysiwygWithReactLink content=
									{
										bannerContent
									}
								/>
							</div>,
						description:
							formProps?.values?.termination_type === TerminationTypes.LeaveOfAbsence &&
							<FormField
								name='acknowledge'
								label={terminationProps?.leaveOfAbsenceAcknowledge}
								component='checkBox'
								touched={formProps.touched}
								errors={formProps.errors}
								value={formProps?.values?.acknowledge ?? false}
								checked={formProps?.values?.acknowledge ?? false}
								required
							/>,
					}} />
				}
			</DividerSection>
		</>
	);
}
